import React, { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { countriesApi } from '../services/api';
import TagPill from './ui/TagPill';

interface Country {
  name: string;
  code: string;
}

interface CountryMultiSelectProps {
  value: string[];
  onChange: (countryCodes: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const CountryMultiSelect: React.FC<CountryMultiSelectProps> = ({
  value,
  onChange,
  placeholder = "Search countries...",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [countries, setCountries] = useState<Country[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<Country[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load countries from API
  useEffect(() => {
    loadCountries();
  }, []);

  // Update selected countries when value changes
  useEffect(() => {
    if (Array.isArray(countries) && countries.length > 0) {
      const selected = countries.filter(country => value.includes(country.code));
      setSelectedCountries(selected);
    }
  }, [value, countries]);

  // Filter countries based on search query
  useEffect(() => {
    const safeCountries = Array.isArray(countries) ? countries : [];
    if (!searchQuery.trim()) {
      setFilteredCountries(safeCountries);
    } else {
      const filtered = safeCountries.filter(country =>
        country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        country.code.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
  }, [searchQuery, countries]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCountries = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const countriesData = await countriesApi.getAll();
      const safeCountries = Array.isArray(countriesData) ? countriesData : [];
      setCountries(safeCountries);
      setFilteredCountries(safeCountries);
    } catch (err) {
      console.error('Failed to load countries:', err);
      setError('Failed to load countries');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCountrySelect = (country: Country) => {
    const isSelected = value.includes(country.code);
    let newValue: string[];
    
    if (isSelected) {
      newValue = value.filter(code => code !== country.code);
    } else {
      newValue = [...value, country.code];
    }
    
    onChange(newValue);
    setSearchQuery('');
    inputRef.current?.focus();
  };

  const handleRemoveCountry = (countryCode: string) => {
    const newValue = value.filter(code => code !== countryCode);
    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredCountries.length > 0) {
        const firstCountry = filteredCountries[0];
        if (!value.includes(firstCountry.code)) {
          handleCountrySelect(firstCountry);
        }
      }
    }
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="country-multi-select">
      {/* Input container with inline pills */}
      <div
        data-testid="country-multi-select-trigger"
        className={`flex flex-wrap items-center gap-1 min-h-[42px] px-3.5 py-1.5 border rounded-full bg-surface cursor-text transition-all ${
          isOpen ? 'border-accent ring-1 ring-accent' : 'border-line hover:border-accent/40'
        } ${disabled ? 'bg-[var(--ghost-bg)] cursor-not-allowed' : ''}`}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {/* Selected country pills inline */}
        {selectedCountries.map((country) => (
          <TagPill
            key={country.code}
            onRemove={() => handleRemoveCountry(country.code)}
            removeLabel={`Remove ${country.name}`}
            disabled={disabled}
          >
            {country.name}
          </TagPill>
        ))}

        {/* Search input */}
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedCountries.length > 0 ? "" : placeholder}
          disabled={disabled}
          className="flex-1 min-w-[60px] outline-none border-none bg-transparent text-sm py-0.5 text-ink"
          style={{ boxShadow: 'none' }}
        />

        <ChevronDownIcon
          className={`h-4 w-4 text-ink-muted flex-shrink-0 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          data-testid="country-multi-select-menu"
          className="absolute z-10 w-full mt-1 bg-surface border border-line rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {isLoading && (
            <div className="px-3 py-2 text-sm text-ink-muted">Loading countries...</div>
          )}

          {error && (
            <div className="px-3 py-2 text-sm text-risk">{error}</div>
          )}

          {!isLoading && !error && filteredCountries.length === 0 && (
            <div className="px-3 py-2 text-sm text-ink-muted">
              {searchQuery ? 'No countries found' : 'No countries available'}
            </div>
          )}

          {!isLoading && !error && filteredCountries.map((country) => {
            const isSelected = value.includes(country.code);
            return (
              <button
                key={country.code}
                type="button"
                onClick={() => handleCountrySelect(country)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--ghost-bg)] flex items-center justify-between ${
                  isSelected ? 'bg-[var(--nav-active-bg)] text-accent-active' : 'text-ink'
                }`}
              >
                <span>{country.name} ({country.code.toUpperCase()})</span>
                {isSelected && (
                  <span className="text-accent-active text-xs">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CountryMultiSelect;